import { AUTH_TOKEN } from '../config';
import { api } from './api';

// access — who is allowed in, as a ladder rather than a boolean.
//
// The first version of this was going to be "members only", and that is exactly
// the shape that has to be replaced the first time something is free, or
// something is for bodies only. So it is a LADDER from the start:
//
//   everyone  no account at all — a visitor who wandered in
//   member    signed in
//   plan      signed in with an ACTIVE paid subscription
//   body      embodied as an avatar that has a built world model
//
// A LADDER, NOT A SET OF FLAGS, because each rung genuinely contains the one
// below: a body costs credits on an avatar that needed a paid plan to exist, and
// a plan needs an account. So "requires: 'plan'" admits bodies too, and nothing
// has to enumerate which levels satisfy which requirement.
//
// ── THIS IS PRESENTATION, NOT SECURITY ───────────────────────────────────────
// Every input here is client-side and forgeable: the token is in localStorage,
// hasPlan comes back over the same wire, and anything below can be set from the
// console. It decides whether a door OPENS and what the guard SAYS. Anything of
// real value behind that door — a stream, a paid recording, a member's data —
// must be refused by the API on its own authority as well. A gate that exists
// only here is a suggestion.
export const LEVELS = ['everyone', 'member', 'plan', 'body'];

export const rank = (level) => {
  const i = LEVELS.indexOf(level || 'everyone');
  // An unrecognised requirement is a typo in a room or a placement row, and the
  // safe reading of "requires: 'planX'" is the strictest one — silently admitting
  // everybody is how a gate turns out never to have worked.
  return i === -1 ? LEVELS.length : i;
};

export const meets = (has, needs) => rank(has) >= rank(needs);

// What to do about it, per rung. `/plans` is the page for both cases: it is
// where an account is bought, and a visitor with no account needs to see the
// same thing before they can be told to sign in.
const PLANS_URL = 'https://www.hypetunes.com/plans';

// WHAT THE REFUSAL SAYS, which is a different question from what the rule is.
// Keyed by what the viewer HAS, because "you need a plan" reads very differently
// to somebody with no account than to somebody who is signed in and already
// paying attention.
export function refusalFor(has, needs) {
  if (needs === 'body') {
    return has === 'everyone' || has === 'member'
      ? { line: 'Members only past this point.', cta: 'See plans', url: PLANS_URL }
      : { line: "You'll need a body before I can let you in.", cta: 'Get a body', url: null };
  }
  if (has === 'everyone') {
    return { line: "This one's for members. Come back when you're on the list.", cta: 'Join HypeTunes', url: PLANS_URL };
  }
  return { line: "Members only. You'll want a plan for this one.", cta: 'See plans', url: PLANS_URL };
}

// ── RESOLVING THE VIEWER'S OWN RUNG ──────────────────────────────────────────
// Cached on the module, because the answer changes at most twice in a session
// (sign in, buy a plan) and the door is a thing you walk into repeatedly. A
// fetch per approach would put a network round trip inside a movement gate.
let cached = null;
let inflight = null;

export function cachedLevel() { return cached; }

// `avatarId` is who you are currently appearing as. Passed in rather than read
// from the store so this stays a plain service — and it matters, because "do you
// have a body" is a question about the avatar you are WEARING, not about the
// best avatar you happen to own.
export async function resolveLevel(avatarId = null) {
  if (!AUTH_TOKEN) { cached = 'everyone'; return cached; }
  if (inflight) return inflight;

  inflight = (async () => {
    let level = 'member';
    try {
      const me = await api.me();
      if (me?.user?.hasPlan) level = 'plan';
    } catch (err) {
      // A FAILED CHECK IS NOT A PASS. Falling through to the top rung on a 500
      // would mean the gate silently opens for everyone the moment the API has a
      // bad minute, and nobody would notice — the failure looks like success.
      console.warn(`[access] could not read plan status (${err.message}) — treating as signed-in only`);
    }
    // The top rung only matters once you are wearing something, and asking about
    // a body you have not chosen yet has no answer.
    if (level === 'plan' && avatarId) {
      try {
        const entry = await api.avatarEntry(avatarId);
        if (entry?.entry?.status === 'ready') level = 'body';
      } catch { /* no row yet is the normal case, and it means no body */ }
    }
    cached = level;
    console.log(`[access] viewer is "${level}"`);
    return level;
  })();

  try { return await inflight; } finally { inflight = null; }
}

// Sign-in and embodiment both invalidate the answer.
export function forgetLevel() { cached = null; forgetStory(); }

// ── THE STORY GATE ───────────────────────────────────────────────────────────
// A second doorman, on a different rule. The ladder above asks what you have
// PAID for; this asks what you have DONE — Khari, 2026-08-03: "no entry to the
// studio until you have 1: had the ride with Spor1, 2: gone to the salon and
// created your presence."
//
// Kept separate rather than bolted onto LEVELS because they are not the same
// axis: a paying member with a body still has not met Spor1 on their first
// night, and a rung that mixed the two would have to be re-explained every time
// either rule moved.
//
// Server-answered on purpose. Both beats leave fields on the profile, so the
// door can ask the profile instead of trusting a client flag — which is what
// makes it survive the guest path, where the ride happens before the avatar
// that would have recorded it even exists.
let story = null;
let storyInflight = null;

export function cachedStory() { return story; }
export function forgetStory() { story = null; }

export async function resolveStory(avatarId = null) {
  // TEST SWITCH (Khari, 2026-08-03: "I have tried 3 sessions and I do not see
  // him" — every account he owns has already done the ride and the salon, so
  // the door correctly let him through every time). `?gate=lock` forces the
  // refusal without needing a fresh account to be turned away with; `?gate=open`
  // forces the pass. Loud in the console, because a forced gate that looks like
  // a real one is how you ship a door nobody tested.
  try {
    const forced = new URLSearchParams(window.location.search).get('gate');
    if (forced === 'lock' || forced === 'open') {
      const open = forced === 'open';
      story = { ride: open, look: open, open, forced: true };
      console.warn(`[access] STORY GATE FORCED ${forced.toUpperCase()} by ?gate= — not a real answer`);
      return story;
    }
  } catch { /* no window/search is fine */ }
  // NOT ASKED IS NOT UNKNOWN (Khari, 2026-08-03: "no gating with a new user. i
  // was let right in"). A visitor with no account CANNOT have been to the salon
  // — it charges, so it requires one — and somebody not wearing an avatar has
  // no profile the ride could have written to. Those are ANSWERS, not gaps, and
  // treating them as gaps is what let a brand-new user walk into the studio.
  // Only a FAILED FETCH is genuinely unknown, and only that opens the door.
  if (!AUTH_TOKEN) {
    story = { ride: false, look: false, open: false, reason: 'guest' };
    console.log('[access] story gate: no account — studio CLOSED');
    return story;
  }
  if (!avatarId) {
    story = { ride: false, look: false, open: false, reason: 'not-embodied' };
    console.log('[access] story gate: not embodied yet — studio CLOSED (re-checks on embodiment)');
    return story;
  }
  if (story) return story;
  if (storyInflight) return storyInflight;
  storyInflight = (async () => {
    try {
      const d = await api.storyGate(avatarId);
      // THE SERVER OWNS THE WORDS when it has them (migration 280): the refusal
      // is composed from the first unmet step's own copy, so a gate of any depth
      // says the one true sentence and this client never has to know the ladder.
      // The hardcoded pair below stays as the pre-migration fallback.
      story = {
        ride: !!d?.ride, look: !!d?.look, open: !!d?.open,
        line: d?.line || null, cta: d?.cta || null, steps: d?.steps || [],
      };
      // One line that answers "why did/didn't the doorman appear".
      console.log(`[access] story gate for avatar ${avatarId}: ride=${story.ride} look=${story.look} → studio ${story.open ? 'OPEN' : 'CLOSED'}`);
    } catch (err) {
      // A FAILED CHECK IS NOT A PASS, same as the ladder — but it is also not a
      // wall: an API blip must not lock someone out of the room their whole
      // session is about. Null means "unknown", and the door treats unknown as
      // open rather than inventing a refusal it cannot justify.
      console.warn(`[access] could not read story gate (${err.message}) — door stays open`);
      story = null;
    }
    return story;
  })();
  try { return await storyInflight; } finally { storyInflight = null; }
}

// What the studio's man on the door says. Ordered, because the answer to "why
// can't I come in" is always the NEXT thing you have to do, never the list.
export function storyRefusal(st) {
  if (!st || st.open) return null;
  // Server-composed copy wins — it knows how many steps the gate has and which
  // one you are on. Everything below is the fallback for a database that has
  // not been migrated yet, and for the two locally-known cases (no account, not
  // embodied) that never reach the server at all.
  if (st.line) return { line: st.line, cta: st.cta || 'Got it', url: null };
  if (st.reason === 'not-embodied') {
    return { line: "Can't let you in till I know who you are. Pick who you're being tonight, then come back.", cta: 'Choose your artist', url: null };
  }
  if (!st.ride) {
    return { line: "Nobody walks into this room off the street. Spor1's bringing you in — go take that ride first.", cta: 'Find Spor1', url: null };
  }
  return { line: "Not looking like that, you don't. Salon's up the block — get right, then come see me.", cta: 'Go to the salon', url: null };
}
